import { Vector3 } from '@babylonjs/core/Maths/math';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { UtilityLayerRenderer } from '@babylonjs/core/Rendering/utilityLayerRenderer';
import { MirrorFeature } from '@models/classes/features/mirror-feature';
import { SliceFeature } from '@models/classes/features/slice-feature';
import { SubmeshData } from '@models/classes/features/submesh-feature';
import { Token } from '@models/classes/token';
import { ITrackedClass } from '@models/interfaces';
import { SubmeshVertexData } from '@view/helpers/geometry-helper';
import { rotationToVector } from '@view/helpers/math-helper';
import { createMirrorVisualization } from '@view/helpers/mirror-helper';
import { FeatureRenderer } from './feature';
export class SliceFeatureRenderer extends FeatureRenderer<SliceFeature> {
    modelToken = Token.SliceFeature;
    previewLayer: UtilityLayerRenderer = this.viewer.utilityLayer;
    babylonMirrorPreviewsMap = new Map<string, Mesh>();

    updateMeshGeometry(
        feature: SliceFeature,
        vertexData: SubmeshVertexData,
        vertexDataChanges: Partial<SubmeshVertexData>
    ) {
        const newVertexData = super.updateMeshGeometry(feature, vertexData, vertexDataChanges);

        if (
            newVertexData.positions &&
            newVertexData.positions.length > 0 &&
            newVertexData.indices &&
            newVertexData.indices.length > 0
        ) {
            const positions = newVertexData.positions;
            const normals = newVertexData.normals;
            const uvs = newVertexData.uvs;
            const indices = newVertexData.indices;

            const triCount = indices.length / 3;
            const positionsCount = positions.length / 3;
            const planeNormal = feature.normal.toVec3();
            const planePoint = feature.intersect.toVec3();

            const newPositions: number[] = [];
            const newNormals: number[] = [];
            const newUvs: number[] = [];
            const newIndices: number[] = [];
            const preSliceSubmeshes = newVertexData.submeshData!;
            const newSubmeshes: SubmeshData[] = preSliceSubmeshes.map(submesh => ({ ...submesh }));
            const edgeSplits: number[] = []; // which vertices are part of how many edges being split
            const edgeStart = Vector3.Zero();
            const edgeDir = Vector3.Zero();
            const workingVector = Vector3.Zero();

            let j: number;
            for (let i = 0; i < triCount; i++) {
                let hit = 0;
                let d = 0;
                let vertexIndex = -1;
                for (j = 0; j < 3; j++) {
                    const iA = j,
                        iB = (j + 1) % 3;
                    const vA = indices[i * 3 + iA],
                        vB = indices[i * 3 + iB];
                    if (vertexIndex === -1) {
                        vertexIndex = vA;
                    }
                    edgeStart.set(positions[vA * 3], positions[vA * 3 + 1], positions[vA * 3 + 2]);
                    edgeStart.subtractFromFloatsToRef(
                        positions[vB * 3],
                        positions[vB * 3 + 1],
                        positions[vB * 3 + 2],
                        edgeDir
                    );
                    edgeDir.scaleInPlace(-1);

                    const lnDot = Vector3.Dot(planeNormal, edgeDir);
                    if (lnDot === 0) {
                        continue;
                    }

                    planePoint.subtractToRef(edgeStart, workingVector);
                    d = Vector3.Dot(workingVector, planeNormal) / lnDot;

                    // Expect floating point errors. don't want unnecessary tiny triangles
                    // If d is between 0 and 1, then the plane hit the edge
                    if (d > 1e-12 && d < 1 - 1e-12) {
                        hit++;
                        edgeSplits[iA] = (edgeSplits[iA] || 0) + 1;
                        edgeSplits[iB] = (edgeSplits[iB] || 0) + 1;
                        edgeDir.scaleInPlace(d);
                        edgeStart.addToRef(edgeDir, workingVector);

                        newPositions.push(workingVector.x, workingVector.y, workingVector.z);

                        if (normals) {
                            workingVector.set(normals[vA * 3], normals[vA * 3 + 1], normals[vA * 3 + 2]);
                            workingVector.scaleInPlace(1 - d);
                            workingVector.addInPlaceFromFloats(
                                normals[vB * 3] * d,
                                normals[vB * 3 + 1] * d,
                                normals[vB * 3 + 2] * d
                            );
                            newNormals.push(workingVector.x, workingVector.y, workingVector.z);
                        }

                        if (uvs) {
                            workingVector.set(uvs[vA * 2], uvs[vA * 2 + 1], 0);
                            workingVector.scaleInPlace(1 - d);
                            workingVector.addInPlaceFromFloats(uvs[vB * 2] * d, uvs[vB * 2 + 1] * d, 0);
                            newUvs.push(workingVector.x, workingVector.y);
                        }
                    }
                }

                if (hit) {
                    // divide the triangle here
                    if (hit === 1) {
                        const split = positionsCount - 1 + newPositions.length / 3;

                        if (!edgeSplits[0]) {
                            newIndices.push(
                                indices[i * 3],
                                indices[i * 3 + 1],
                                split,
                                indices[i * 3],
                                split,
                                indices[i * 3 + 2]
                            );
                        } else if (!edgeSplits[1]) {
                            newIndices.push(
                                indices[i * 3],
                                indices[i * 3 + 1],
                                split,
                                indices[i * 3 + 1],
                                indices[i * 3 + 2],
                                split
                            );
                        } else {
                            newIndices.push(
                                indices[i * 3],
                                split,
                                indices[i * 3 + 2],
                                indices[i * 3 + 1],
                                indices[i * 3 + 2],
                                split
                            );
                        }
                        newSubmeshes.forEach((submeshData, sI) => {
                            const preSliceSubmesh = preSliceSubmeshes[sI];
                            if (
                                preSliceSubmesh.indexStart <= i * 3 &&
                                preSliceSubmesh.indexStart + preSliceSubmesh.indexCount > i * 3
                            ) {
                                submeshData.indexCount += 3;
                            } else if (preSliceSubmesh.indexStart > i * 3) {
                                submeshData.indexStart += 3;
                            }
                        });
                    }
                    if (hit === 2) {
                        const aSplit = positionsCount - 1 + newPositions.length / 3 - 1,
                            bSplit = positionsCount - 1 + newPositions.length / 3;
                        newIndices.push(indices[i * 3]);
                        newIndices.push(aSplit);
                        newIndices.push(bSplit);

                        if (edgeSplits[0] === 2) {
                            newIndices.push(
                                indices[i * 3 + 1],
                                bSplit,
                                aSplit,
                                indices[i * 3 + 2],
                                bSplit,
                                indices[i * 3 + 1]
                            );
                        } else if (edgeSplits[1] === 2) {
                            newIndices.push(
                                indices[i * 3 + 2],
                                indices[i * 3],
                                bSplit,
                                indices[i * 3 + 1],
                                bSplit,
                                aSplit
                            );
                        } else {
                            newIndices.push(
                                indices[i * 3 + 1],
                                aSplit,
                                indices[i * 3],
                                indices[i * 3 + 2],
                                bSplit,
                                aSplit
                            );
                        }
                        newSubmeshes.forEach((submeshData, sI) => {
                            const preSliceSubmesh = preSliceSubmeshes[sI];
                            if (
                                preSliceSubmesh.indexStart <= i * 3 &&
                                preSliceSubmesh.indexStart + preSliceSubmesh.indexCount > i * 3
                            ) {
                                submeshData.indexCount += 6;
                            } else if (preSliceSubmesh.indexStart > i * 3) {
                                submeshData.indexStart += 6;
                            }
                        });
                    }
                    edgeSplits.clear();
                    hit = 0;
                } else {
                    // if(feature.removeGeometry && d > 1 - 1e-12) {
                    //     // If this face is in front of the face relative to the face normal, cull it
                    //     newSubmeshes.forEach(submeshData => {
                    //         if(submeshData.indexStart <= i && submeshData.indexStart + submeshData.indexCount > i) {
                    //             submeshData.indexCount -= 3;
                    //         } else if(submeshData.indexStart > i) {
                    //             submeshData.indexStart -= 3;
                    //         }
                    //     });
                    //     continue;
                    // } else {
                    //     console.log(d);
                    // }
                    newIndices.push(indices[i * 3], indices[i * 3 + 1], indices[i * 3 + 2]);
                }

                // Update the vertices the submeshes are targetting
                if (vertexIndex !== -1 && hit > 0) {
                    newSubmeshes.forEach(submeshData => {
                        if (
                            submeshData.verticesStart <= vertexIndex &&
                            submeshData.verticesStart + submeshData.verticesCount > vertexIndex
                        ) {
                            submeshData.verticesCount += hit;
                        } else if (submeshData.verticesStart > vertexIndex) {
                            submeshData.verticesStart += hit;
                        }
                    });
                }
            }

            newVertexData.indices = new Int32Array(newIndices);

            newVertexData.positions = new Float32Array(positions.length + newPositions.length);
            newVertexData.positions.set(positions);
            newVertexData.positions.set(newPositions, positions.length);

            if (normals) {
                newVertexData.normals = new Float32Array(normals.length + newNormals.length);
                newVertexData.normals.set(normals);
                newVertexData.normals.set(newNormals, normals.length);
            }

            if (uvs) {
                newVertexData.uvs = new Float32Array(uvs.length + newUvs.length);
                newVertexData.uvs.set(uvs);
                newVertexData.uvs.set(newUvs, uvs.length);
            }

            // Remove geometry on one side of the plane if the removeGeometry flag is true
            const baseGeometryVertexLength = positions.length / 3;
            if (feature.removeGeometry) {
                const indices = newVertexData.indices;
                const positions = newVertexData.positions;
                const normals = newVertexData.normals;
                const uvs = newVertexData.uvs;
                const newIndices = new Int32Array(indices.length);
                const newPositions = new Float32Array(positions.length);
                const newNormals = new Float32Array(normals?.length || 0);
                const newUvs = new Float32Array(uvs?.length || 0);

                const triCount = indices.length / 3;
                const vertexCount = positions.length / 3;
                const newPositionIndex = new Int32Array(vertexCount);

                // Keep track of how many positions we've used
                let position = 0;
                for (let i = 0; i < vertexCount; i++) {
                    workingVector.set(positions[i * 3], positions[i * 3 + 1], positions[i * 3 + 2]);
                    workingVector.subtractInPlace(planePoint);
                    const d = Vector3.Dot(workingVector, planeNormal);

                    // Don't remove new vertices that were created from the slice, those are guaranteed to be good
                    if (i < baseGeometryVertexLength && d > 1e-12) {
                        // Mark the original indices that have been removed so we can update the index array
                        newPositionIndex[i] = -1;
                    } else {
                        // Copy over the vertex data we need and mark what new index they will have
                        newPositionIndex[i] = position;
                        newPositions[position * 3] = positions[i * 3];
                        newPositions[position * 3 + 1] = positions[i * 3 + 1];
                        newPositions[position * 3 + 2] = positions[i * 3 + 2];
                        if (normals) {
                            newNormals[position * 3] = normals[i * 3];
                            newNormals[position * 3 + 1] = normals[i * 3 + 1];
                            newNormals[position * 3 + 2] = normals[i * 3 + 2];
                        }
                        if (uvs) {
                            newUvs[position * 2] = uvs[i * 2];
                            newUvs[position * 2 + 1] = uvs[i * 2 + 1];
                        }
                        position++;
                    }
                }
                // Trim off unused parts of the vertex arrays
                newVertexData.positions = newPositions.subarray(0, position * 3);
                if (normals) {
                    newVertexData.normals = newNormals.subarray(0, position * 3);
                }
                if (uvs) {
                    newVertexData.uvs = newUvs.subarray(0, position * 2);
                }

                position = 0;
                const originalSubmeshes = newSubmeshes.map(submesh => ({ ...submesh }));
                for (let i = 0; i < triCount; i++) {
                    // If all of the positions in this triangle are being kept, add the triangle
                    // The indices of the vertexes are changing so we need to use the value that we calculated prior
                    if (
                        newPositionIndex[indices[i * 3]] >= 0 &&
                        newPositionIndex[indices[i * 3 + 1]] >= 0 &&
                        newPositionIndex[indices[i * 3 + 2]] >= 0
                    ) {
                        newIndices[position * 3] = newPositionIndex[indices[i * 3]];
                        newIndices[position * 3 + 1] = newPositionIndex[indices[i * 3 + 1]];
                        newIndices[position * 3 + 2] = newPositionIndex[indices[i * 3 + 2]];
                        position++;
                    } else {
                        newSubmeshes.forEach((submeshData, sI) => {
                            const originalSubmesh = originalSubmeshes[sI];
                            if (
                                originalSubmesh.indexStart <= i * 3 &&
                                originalSubmesh.indexStart + originalSubmesh.indexCount > i * 3
                            ) {
                                submeshData.indexCount -= 3;
                            } else if (originalSubmesh.indexStart > i * 3) {
                                submeshData.indexStart -= 3;
                            }
                        });
                    }
                }
                // Update the vertex values on the submeshes as well
                for (let i = 0; i < newPositionIndex.length; i++) {
                    if (newPositionIndex[i] === -1) {
                        newSubmeshes.forEach(submeshData => {
                            if (
                                submeshData.verticesStart <= i &&
                                submeshData.verticesStart + submeshData.verticesCount > i
                            ) {
                                submeshData.verticesCount--;
                            } else if (submeshData.verticesStart > i) {
                                submeshData.verticesStart--;
                            }
                        });
                    }
                }
                newVertexData.indices = newIndices.subarray(0, position * 3);
            }

            newVertexData.submeshData = newSubmeshes;
        }
        return newVertexData;
    }

    protected updatePreviewMesh(feature: SliceFeature) {
        const scene = this.previewLayer.utilityLayerScene;
        let visualizationPlane = this.babylonMirrorPreviewsMap.get(feature.id);

        if (!visualizationPlane) {
            visualizationPlane = createMirrorVisualization(feature, scene);
            visualizationPlane.isPickable = false;
            this.babylonMirrorPreviewsMap.set(feature.id, visualizationPlane);
        }

        const currentNormal = new Vector3();
        currentNormal.set(0, 0, 1);
        const newNormal = new Vector3();
        newNormal.set(feature.normal.x, feature.normal.y, feature.normal.z);
        visualizationPlane.rotationQuaternion = rotationToVector(newNormal, currentNormal);
        let parentBoundingBox = undefined;
        if (feature._parent) {
            parentBoundingBox = this.viewer.getMeshBoundingBox(feature._parent);
        }
        if (parentBoundingBox) {
            const scale = parentBoundingBox.minWorld.subtract(parentBoundingBox.maxWorld).length();
            visualizationPlane.scaling.set(scale, scale, scale);
        }

        if (feature._parent) {
            const featureParent = this.viewer.getRendererNode(feature._parent);
            const meshPosition = featureParent.getAbsolutePosition();
            meshPosition.addInPlace(feature.intersect.toVec3());
            visualizationPlane.position.set(meshPosition.x, meshPosition.y, meshPosition.z);
        }
    }

    update3dInternal(node: ITrackedClass<SliceFeature>) {
        const visualizationPlane = this.babylonMirrorPreviewsMap.get(node.id);

        if (node._previewVisible) {
            this.updatePreviewMesh(node);
            if (node.enabled) {
                visualizationPlane?.setEnabled(true);
            }
        } else {
            visualizationPlane?.setEnabled(false);
        }
    }

    delete3d(node: ITrackedClass<SliceFeature> | ITrackedClass<MirrorFeature>) {
        if (this.babylonMirrorPreviewsMap) {
            const featureInstance = this.babylonMirrorPreviewsMap.get(node.id);

            if (featureInstance) {
                featureInstance.dispose(false, true);
            }
            this.babylonMirrorPreviewsMap.delete(node.id);
        }
        super.delete3d(node as ITrackedClass<SliceFeature>);
    }
}
